import React, { useEffect, useState } from 'react';
import { Formik, Form, FormikHelpers as FormikActions } from 'formik';
import { cloneDeep } from 'lodash'

import { APIError, handle_drf_form_error } from 'django-rest-react';
import { displayUser, displayBadgePermesso } from '../utils';
import { login_url } from '../globals';
import {
  FormGroupBootstrap, ErrorBlockGeneric,
  ConfirmedButton, CategoriaSelector, CategoriaEventoMap
} from '../components';
import {
  update_profile, update_avatar, update_avatar_serio,
} from '../api';
import { useLogin } from '../reducers';
import './Profile.scss';

interface FormValues {
  categorie_preferite: number[],
  ricevi_email: boolean,
  website: string,
  avatar: any,
  avatar_serio: any,
}

type AvatarData = Parameters<typeof update_avatar>[0];

const Profile = (props: {}) => {

  const login = useLogin();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Profilo - Owlitrack";
  }, [])


  if (!login.logged_in) {
    login_url();
  }

  const handle_avatar_helper = async (func: typeof update_avatar | typeof update_avatar_serio,
    values: AvatarData, actions: FormikActions<AvatarData>) => {
    func(values)
      .then(() => window.location.reload())
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
      .finally(() => actions.setSubmitting(false))
  }

  const handleSubmit = async (values: FormValues, actions: FormikActions<FormValues>) => {
    let submitting_data = cloneDeep(values);
    delete submitting_data.avatar;
    delete submitting_data.avatar_serio;
    update_profile(submitting_data)
      .then(() => window.location.reload())
      .catch((error: APIError) => handle_drf_form_error(error, actions, setError))
      .finally(() => actions.setSubmitting(false))
  }


  const initial = {
    categorie_preferite: login.user.categorie_preferite,
    ricevi_email: login.user.ricevi_email,
    website: login.user.website,
  }
  const empty_data = { _file: null as unknown as File };
  return (
    <div className="container profile-page">
      <h1 className="page-header">Il tuo profilo</h1>
      <section className="profile-summary" aria-label="Riepilogo del profilo">
        <div className="profile-identity">
          {login.user.avatar ? <img src={`/avatars/${login.user.avatar}/`}
            alt={displayUser(login.user.user)} className="profile-avatar" />
            : <span className="profile-avatar profile-avatar-placeholder" aria-hidden="true"><i className="fa fa-user" /></span>}
          <div>
            <h2>{displayUser(login.user.user)}</h2>
            <div className="profile-permission"><span>Permessi</span> {displayBadgePermesso(login.user.permessi)}</div>
          </div>
        </div>
        <dl className="profile-summary-details">
          <div>
            <dt>Eventi assegnati</dt>
            <dd className="profile-event-badges">{login.user.eventi_assegnati.length
              ? <CategoriaEventoMap cats={login.user.eventi_assegnati} />
              : <span className="text-muted">Nessun evento assegnato.</span>}</dd>
          </div>
        </dl>
      </section>

      <ErrorBlockGeneric error={error} />
      <div className="profile-settings">
        <section className="profile-card" aria-labelledby="profile-preferences-heading">
          <div className="profile-card-heading">
            <span className="profile-card-icon" aria-hidden="true"><i className="fa fa-sliders-h" /></span>
            <h2 id="profile-preferences-heading">Preferenze e notifiche</h2>
          </div>
          <Formik initialValues={initial} onSubmit={handleSubmit}>
            {({ isSubmitting, handleReset }) => <Form className="form needs-validation profile-form" aria-label="Preferenze e notifiche">
              <div className="profile-form-section">
                <CategoriaSelector name="categorie_preferite" displayName="Categorie preferite"
                  help_text="Seleziona le categorie che preferisci seguire." />
              </div>
              <div className="profile-form-section">
                <FormGroupBootstrap name="ricevi_email" type="checkbox" displayName="Notifiche email"
                  help_text="Ricevi email di notifica sui post in cui sei coinvolto" />
              </div>
              <FormGroupBootstrap name="website" type="text" displayName="Sito web"
                help_text="Il tuo sito web, pubblicato su Gascal se comparirai nei top contributors." />
              <div className="profile-actions">
                <ConfirmedButton type="warning" onSubmit={handleReset}>Reset</ConfirmedButton>
                <button type="submit" disabled={isSubmitting} className="btn btn-primary">Salva preferenze</button>
              </div>
            </Form>}
          </Formik>
        </section>

        <div className="profile-avatar-settings">
          <section className="profile-card" aria-labelledby="profile-avatar-heading">
            <div className="profile-card-heading">
              <span className="profile-card-icon" aria-hidden="true"><i className="fa fa-user-circle" /></span>
              <h2 id="profile-avatar-heading">Avatar su Owlitrack</h2>
            </div>
            <div className="profile-avatar-description">
              {login.user.avatar ? <img src={`/avatars/${login.user.avatar}/`}
                alt="Avatar attuale su Owlitrack" className="profile-avatar-preview" />
                : <span className="profile-avatar-preview profile-avatar-placeholder" aria-hidden="true"><i className="fa fa-user" /></span>}
              <p className="profile-help">L'immagine che ti rappresenta su Owlitrack.</p>
            </div>
            <Formik initialValues={empty_data}
              onSubmit={(values, actions) => handle_avatar_helper(update_avatar, values, actions)}>
              {({ isSubmitting, handleReset }) => <Form className="form needs-validation profile-form" aria-label="Avatar su Owlitrack">
                <FormGroupBootstrap name="_file" type="file" displayName="File" />
                <div className="profile-actions">
                  <ConfirmedButton type="warning" onSubmit={handleReset}>Reset</ConfirmedButton>
                  <button type="submit" disabled={isSubmitting} className="btn btn-primary">Aggiorna avatar</button>
                </div>
              </Form>}
            </Formik>
          </section>

          <section className="profile-card" aria-labelledby="profile-gascal-heading">
            <div className="profile-card-heading">
              <span className="profile-card-icon" aria-hidden="true"><i className="fa fa-camera" /></span>
              <h2 id="profile-gascal-heading">Foto per Gascal</h2>
            </div>
            <div className="profile-avatar-description">
              {login.user.avatar_serio ? <img src={`/avatars-seri/${login.user.avatar_serio}/`}
                alt="Foto attuale per Gascal" className="profile-avatar-preview profile-avatar-preview--square" />
                : <span className="profile-avatar-preview profile-avatar-placeholder" aria-hidden="true"><i className="fa fa-camera" /></span>}
              <div className="profile-help">
                <p>Questa foto sarà pubblicata su Gascal se comparirai nei top contributors. Scegli una tua foto semi-seria e decorosa.</p>
                <p>Verrà ritagliata automaticamente in formato quadrato: usa una foto adatta al ritaglio.</p>
                {!login.user.avatar_serio && <p>Non hai impostato un avatar serio.</p>}
              </div>
            </div>
            <Formik initialValues={empty_data}
              onSubmit={(values, actions) => handle_avatar_helper(update_avatar_serio, values, actions)}>
              {({ isSubmitting, handleReset }) => <Form className="form needs-validation profile-form" aria-label="Foto per Gascal">
                <FormGroupBootstrap name="_file" type="file" displayName="File" />
                <div className="profile-actions">
                  <ConfirmedButton type="warning" onSubmit={handleReset}>Reset</ConfirmedButton>
                  <button type="submit" disabled={isSubmitting} className="btn btn-primary">Aggiorna foto</button>
                </div>
              </Form>}
            </Formik>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Profile;
